<template>
  <div class="audio-widget p-3" style="border-radius: 4px;">
    <div class="is-flex is-align-items-center mb-1">
      <span class="has-text-weight-bold is-size-7 is-uppercase">{{ props.label }}</span>
    </div>
    
    <div class="audio-meter-track mb-1" v-for="ch in 2" :key="ch">
      <div class="meter-gradient-bg" :style="{ background: meterGradient }"></div>
      <!-- Blocker revealing the gradient from left to right -->
      <div class="meter-blocker" :style="{ left: `${displayLevel}%` }"></div>
      <div class="peak-marker" :style="{ left: `${peakLevel}%` }"></div>
    </div>

    <!-- Aligned Scale -->
    <div class="meter-scale-container mt-1">
      <div 
        v-for="db in [-60, -45, -30, -20, -10, -5, 0]" 
        :key="db"
        class="scale-tick"
        :style="{ left: `${getDbPercentage(db)}%` }"
      >
        <span class="tick-mark">|</span>
        <span class="tick-label">{{ db }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted, watch } from 'vue';

const props = defineProps({ stream: MediaStream, label: String });
const MIN_DB = -60;
const MAX_DB = 0;

const displayLevel = ref(0);
const peakLevel = ref(0);
const currentDb = ref(MIN_DB);

let audioCtx = null;
let analyser = null;
let dataArray = null;
let animationId = null;

const getDbPercentage = (db) => {
  const safeDb = Math.max(MIN_DB, Math.min(MAX_DB, db));
  return ((safeDb - MIN_DB) / (MAX_DB - MIN_DB)) * 100;
};

const meterGradient = computed(() => {
  const yellowPos = getDbPercentage(-18);
  const redPos = getDbPercentage(-6);
  return `linear-gradient(90deg, #48c774 0%, #48c774 ${yellowPos}%, #ffdd57 ${yellowPos}%, #ffdd57 ${redPos}%, #f14668 ${redPos}%, #f14668 100%)`;
});

const processAudio = () => {
  if (!analyser) return;
  analyser.getByteTimeDomainData(dataArray);

  let maxVal = 0;
  for (let i = 0; i < dataArray.length; i++) {
    const amplitude = Math.abs(dataArray[i] - 128) / 128;
    if (amplitude > maxVal) maxVal = amplitude;
  }

  const db = 20 * Math.log10(maxVal || 0.000001);
  currentDb.value = Math.max(MIN_DB, db);
  const targetLevel = getDbPercentage(currentDb.value);

  // BBC PPM Ballistics
  if (targetLevel > displayLevel.value) {
    displayLevel.value += (targetLevel - displayLevel.value) * 0.25; // Attack
  } else {
    displayLevel.value = Math.max(0, displayLevel.value - 0.7); // Decay (~2.8s)
  }

  // Peak Hold
  if (displayLevel.value > peakLevel.value) peakLevel.value = displayLevel.value;
  else peakLevel.value = Math.max(0, peakLevel.value - 0.15);

  animationId = requestAnimationFrame(processAudio);
};

const setupAudio = async (stream) => {
  if (!stream) return;
  
  // Cleanup old context if it exists
  if (audioCtx) await audioCtx.close();

  audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  
  // CRITICAL: Ensure the context is running
  if (audioCtx.state === 'suspended') {
    await audioCtx.resume();
  }

  const source = audioCtx.createMediaStreamSource(stream);
  analyser = audioCtx.createAnalyser();
  analyser.fftSize = 256;
  source.connect(analyser);
  
  dataArray = new Uint8Array(analyser.frequencyBinCount);
  processAudio();
};

watch(() => props.stream, (s) => setupAudio(s), { immediate: true });

onUnmounted(() => {
  cancelAnimationFrame(animationId);
  audioCtx?.close();
});
</script>

<style scoped>
.audio-meter-track {
  height: 12px;
  background: #1a1a1a;
  position: relative;
  overflow: hidden;
  border: 1px solid #111;
}
.meter-gradient-bg { position: absolute; inset: 0; }
.meter-blocker {
  position: absolute;
  top: 0; bottom: 0; right: 0;
  /* background: #1a1a1a; /* Matches track background precisely */
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 1;
}
.peak-marker {
  position: absolute;
  width: 2px; height: 100%;
  background: #fff;
  z-index: 2;
  box-shadow: 0 0 2px rgba(0,0,0,0.5);
}
.meter-scale-container { position: relative; height: 20px; width: 100%; }
.scale-tick {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateX(-50%);
}
.tick-mark { color: #555; font-size: 8px; line-height: 1; }
.tick-label { font-size: 10px; font-family: 'Courier New', Courier, monospace; margin-top: 2px; }
</style>
